import styled from '@emotion/styled';
import { PrismicLink, WrapText } from '@landsbankinn/prismic';
import {
  device,
  responseFont,
  typography,
  shouldForwardProp,
} from '@landsbankinn/ui';
import Link from 'next/link';
import React from 'react';
import { animated } from 'react-spring';

export enum TileLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

interface IProps {
  number: string | null;
  title: string | null;
  copy?: React.ReactNode;
  icon: React.ReactNode;
  layout?: TileLayout | null;
  iconName: string;
  [key: string]: any;
}

const Host = styled(animated.div, {
  shouldForwardProp: shouldForwardProp('div'),
})<{ layout?: string | null }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 68px 0;

  margin-bottom: 10px;
  min-height: 95vw;

  @media ${device.tablet} {
    min-height: 201px;
    padding: ${(props) =>
      props.layout === TileLayout.Vertical ? '0' : '27px 0'};
    margin-bottom: 0;

    flex-direction: ${(props) =>
      props.layout === TileLayout.Horizontal ? 'row' : 'column'};

    justify-content: ${(props) =>
      props.layout === TileLayout.Vertical ? 'space-between' : 'center'};
  }

  @media ${device.ipadPro} {
    padding: ${(props) =>
      props.layout === TileLayout.Vertical ? '0' : '44px 0'};
    min-height: 298px;
  }

  @media ${device.desktop} {
    min-height: 380px;
    padding: ${(props) =>
      props.layout === TileLayout.Vertical ? '0 0 40px' : '80px 0'};
  }
`;

const Icon = styled.figure<{ name?: string }>`
  margin-bottom: 10px;
  width: 70px;
  height: 70px;
  ${(props) =>
    props.name === 'blueyoutube' || props.name === 'bluespotify'
      ? 'display:flex;'
      : ''}

  @media ${device.mobile} {
    ${(props) =>
      props.name === 'blueyoutube'
        ? // eslint-disable-next-line sonarjs/no-duplicate-string
          'width:112px;display:flex;'
        : props.name === 'bluespotify'
        ? 'width:112px;display:flex;'
        : `
    width: 112px;
    height: 112px;
    `}
  }
  @media ${device.tablet} {
    ${(props) =>
      props.name === 'blueyoutube'
        ? 'width:112px;display:flex;'
        : props.name === 'bluespotify'
        ? 'width:112px;display:flex;'
        : props.name === 'icelandairwaves'
        ? 'width:112px;'
        : `
    width: 70px;
    height: 70px;
    `}
  }

  @media ${device.ipadPro} {
    ${(props) =>
      props.name === 'blueyoutube'
        ? 'width:112px;display:flex;'
        : props.name === 'bluespotify'
        ? 'width:112px;display:flex;'
        : props.name === 'icelandairwaves'
        ? 'width:112px;'
        : props.name === 'landsbankinn'
        ? 'width:112px;'
        : props.name === 'airwaves'
        ? 'width:112px;'
        : `
    width: 70px;
    height: 70px;
    `}
  }

  @media ${device.desktop} {
    ${(props) =>
      props.name === 'Anaegjuvogin'
        ? 'height:139px;width:92px;margin-bottom:25px;'
        : props.name === 'blueyoutube'
        ? 'width:180px'
        : props.name === 'bluespotify'
        ? 'width:210px'
        : props.name === 'airwaves'
        ? 'width:160px;'
        : props.name === 'landsbankinn'
        ? 'width:80px;'
        : props.name === 'icelandairwaves'
        ? 'width:240px;'
        : `width: 112px;
    height: 112px;`};
  }

  svg {
    object-fit: contain;
    width: 100%;
    height: 100%;
  }
`;

const Top = styled.div<{ layout?: string | null }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 20px;

  margin-bottom: ${(props) =>
    props.layout === TileLayout.Horizontal ||
    props.layout === TileLayout.Vertical
      ? '105px'
      : '0'};

  @media ${device.tablet} {
    padding: 0 44px;
    margin-bottom: 0;
    width: ${(props) =>
      props.layout === TileLayout.Horizontal ? '50%' : 'auto'};
    height: ${(props) =>
      props.layout === TileLayout.Vertical ? 'calc(50% + 15px)' : 'auto'};
  }
`;

const Number = styled.div`
  ${typography.numbers2}
  ${responseFont(30, 55)}
`;

const Title = styled.h2<{ layout?: string | null }>`
  font-weight: bold;
  text-align: center;

  @media ${device.tablet} {
    font-size: 14px;
    text-align: ${(props) =>
      props.layout === TileLayout.Horizontal ? 'left' : 'center'};
  }
  @media ${device.desktop} {
    font-size: 18px;
  }
`;
const Copy = styled.div<{ layout?: string | null }>`
  display: ${(props) => (props.layout ? 'block' : 'none')};
  margin-top: ${(props) =>
    props.layout === TileLayout.Horizontal ||
    props.layout === TileLayout.Vertical
      ? '48px'
      : '10px'};
  text-align: center;
  @media ${device.tablet} {
    margin-top: 20px;
    div {
      font-size: 14px;
    }
    text-align: ${(props) =>
      props.layout === TileLayout.Horizontal ? 'left' : 'center'};
  }
  @media ${device.desktop} {
    div {
      font-size: 18px;
    }
  }
`;

const Bottom = styled.div<{ layout?: string | null }>`
  padding: 0 20px;

  @media ${device.tablet} {
    padding: ${(props) =>
      props.layout === TileLayout.Vertical ? '0 20px' : '0'};
    padding-right: ${(props) =>
      props.layout === TileLayout.Horizontal && '44px'};
    width: ${(props) =>
      props.layout === TileLayout.Horizontal ? '50%' : 'auto'};
    height: ${(props) =>
      props.layout === TileLayout.Vertical && 'calc(50% - 30px)'};
  }
`;

export const TileCard = ({
  number,
  title,
  copy,
  icon,
  layout,
  iconName,
  link,
  ...rest
}: IProps) => {
  return (
    <Host layout={layout} {...rest}>
      <Top layout={layout}>
        {link ? (
          iconName === 'blueyoutube' ||
          iconName === 'bluespotify' ||
          iconName === 'airwaves' ? (
            <a href={link.url} target="_blank" rel="noreferrer">
              <Icon name={iconName}>{icon}</Icon>
            </a>
          ) : (
            <PrismicLink value={link}>
              <Icon name={iconName}>{icon}</Icon>
            </PrismicLink>
          )
        ) : (
          <Icon name={iconName}>{icon}</Icon>
        )}
        <Number>{number}</Number>
      </Top>
      <Bottom layout={layout}>
        <Title layout={layout}>{title}</Title>
        {copy && <Copy layout={layout}>{copy}</Copy>}
      </Bottom>
    </Host>
  );
};
